import './TaxResources.scss';

import { object, bool } from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';

import {
  taxInformationLinks,
  taxPlanningResourceLinks,
  directDeposit529Url,
  linkBuilder
} from 'services/Externals';
import { priorYear, mapStatementsProductName } from 'services/Statements';
import {
  selectStatementsDocumentTypes,
  selectStatementsTimePeriod,
  selectStatementsProductTypes
} from 'state/statements/StatementsActions';
import {
  getHasOwnedCollegeSavingsOnly,
  getNonViewOnlyProducts,
  hasOwnedRetirementAccounts,
  hasOwnedMaryland529Only
} from 'state/account/AccountSelectors';
import {
  getTurboTaxLinkInfo,
  hasRMDEligibleAccounts
} from 'state/taxes/TaxSelectors';
import {
  getHasOwnedCollegeSavings,
  getView,
  hasMultipleOwnedCollegeSavings
} from 'state/view/ViewSelectors';
import Store from 'state/Store';
import { isRMDEligible } from 'state/customer/CustomerSelectors';
import {AAMBeaconButton} from "components/shared/Beacon";

const DisplayTaxResources = (props) => {
  const linkedList = props.taxResourcesList;

  const onTurboTaxFormClick = (path) => {
    props.history.push(path);
  };

  return linkedList.map((link, i) => {
    const { className, featureEnabled, path, target = '_blank', title } = link;

    return featureEnabled ? (
      <div className={`${className}`} key={`tax-resource-${i}`}>
        <AAMBeaconButton variant="tertiary" scale="normal" onBeaconEventClick={() => {onTurboTaxFormClick(path);}}>{title}</AAMBeaconButton>
      </div>
    ) : (
      <div className={`${className}`} key={`tax-resource-${i}`}>
        <AAMBeaconButton variant="tertiary" scale="normal" link-href={linkBuilder(path, props.view)} link-target={target}>{title}</AAMBeaconButton>
      </div>
    );
  });
};

export const TaxResourcesV2 = ({
  history,
  turboTaxLinkInfo,
  view,
  hasOwnedMaryland529Only,
  hasOwnedCollegeSavings,
  hasMultipleOwnedCollegeSavings,
  nonViewOnlyProducts,
  hasOwnedRetirementAccounts,
  hasOwnedCollegeSavingsOnly,
  isRMDEligible,
  hasRMDEligibleAccount
}) => {
  const onTaxFormClick = () => {
    Store.dispatch(selectStatementsDocumentTypes('taxform'));
    Store.dispatch(selectStatementsTimePeriod(priorYear.toString()));
    if (nonViewOnlyProducts.length === 1) {
      const productType = mapStatementsProductName(nonViewOnlyProducts);
      Store.dispatch(selectStatementsProductTypes(productType));
    } else {
      Store.dispatch(selectStatementsProductTypes('all'));
    }
    history.push('/statements?taxPageEntry=true');
  };

  const get529OnlyDirectDepositTitle = (view) => {
    return view.hasOwnedAlaska529Only || view.hasOwnedNational529Only
      ? 'Direct deposit form'
      : 'Tax refund direct deposit';
  };

  const isRMDAccountDetails = isRMDEligible && hasRMDEligibleAccount;

  const TAX_INFORMATION_LIST = [
    {
      className: 'e2e-tax-form-mailing-schedule b-font-weight-light b-typography-sans-serif--body--100',
      path: taxInformationLinks.taxFormMailingSchedule,
      title: 'Tax form mailing schedule'
    },
    {
      className: 'e2e-tax-ira-questions b-font-weight-light b-typography-sans-serif--body--100',
      path: taxInformationLinks.commonTaxAndIRAQuestions,
      title: 'Common tax & IRA questions'
    }
  ];

  const DISTRIBUTIONS_TAX_RESOURCES_LIST = [
    {
      className: 'e2e-dividend-distributions b-font-weight-light b-typography-sans-serif--body--100',
      path: taxPlanningResourceLinks.dividendDistributions,
      title: 'Dividend distributions'
    },
    {
      className: 'e2e-tax-planning-resources b-font-weight-light b-typography-sans-serif--body--100',
      path: taxPlanningResourceLinks.taxPlanningResources,
      title: 'Tax planning center'
    }
  ];

  const TURBO_TAX_LINK = [
    {
      className: 'e2e-turbo-tax-v2 b-font-weight-bold b-typography-sans-serif--body--50',
      path: '/turbotax-benefits',
      title: turboTaxLinkInfo.linkText,
      featureEnabled: true
    }
  ];

  const RMD_LINK = [
    {
      className: 'e2e-rmd',
      path: '/usis/rmd-tool',
      title: 'Manage my required minimum distribution (RMD)',
      target: '_self'
    }
  ];

  const TAX_REFUND_DIRECT_DEPOSIT_529_ONLY_LINK = [
    {
      className: 'e2e-tax-planning-resources',
      path: directDeposit529Url(view),
      title: get529OnlyDirectDepositTitle(view)
    }
  ];

  const TAX_REFUND_DIRECT_DEPOSIT_HAS_529_LINK = [
    {
      className: 'e2e-college-savings-tax-refund',
      path: directDeposit529Url(view),
      title: '529 College savings plan tax refund direct deposit form'
    }
  ];

  const MyTaxInformation = () => (
    <Fragment>
      <h1 className="tile-summary-header-v2 b-font-weight-bold b-typography-sans-serif--headline-loose--100">My tax information</h1>
      <div className={`beacon-on-light`}>
        <AAMBeaconButton
            className="e2e-tax-form-info-v2 b-font-weight-light b-typography-sans-serif--body--100"
            variant="tertiary"
            scale="normal"
            aria-label="My tax forms link"
            data-testid="my-tax-forms-button-link"
            onBeaconEventClick={() => {
              onTaxFormClick();
            }}
        >
          My tax forms
        </AAMBeaconButton>
      </div>
      <DisplayTaxResources
        taxResourcesList={TAX_INFORMATION_LIST}
        view={view}
      />
    </Fragment>
  );

  const TaxPlanningDefault = () => (
    <Fragment>
      <h1 className={
        hasOwnedRetirementAccounts
            ? "tile-summary-secondary-header-v2 b-font-weight-bold b-typography-sans-serif--body-loose--200"
            : "tile-summary-header-v2 b-font-weight-bold b-typography-sans-serif--headline-loose--100"
      }>
        Tax planning resources
      </h1>
      <DisplayTaxResources
        taxResourcesList={DISTRIBUTIONS_TAX_RESOURCES_LIST}
        view={view}
      />
      <DisplayTaxResources taxResourcesList={TURBO_TAX_LINK} view={view} history={history} />
      {isRMDAccountDetails && (
        <DisplayTaxResources taxResourcesList={RMD_LINK} view={view} />
      )}
    </Fragment>
  );

  const TaxPlanning529 = ({ hasMultipleOwnedCollegeSavings }) => {
    const taxPlanning529Only = (
      <Fragment>
        <h1>Tax Planning Resources</h1>
        <DisplayTaxResources
          taxResourcesList={TAX_REFUND_DIRECT_DEPOSIT_529_ONLY_LINK}
          view={view}
        />
        {!hasOwnedMaryland529Only && (
          <DisplayTaxResources taxResourcesList={TURBO_TAX_LINK} view={view} history={history} />
        )}
        {isRMDAccountDetails && (
          <DisplayTaxResources taxResourcesList={RMD_LINK} view={view} />
        )}
      </Fragment>
    );

    const taxPlanningHasCollegeSavings = (
      <Fragment>
        <TaxPlanningDefault />
        {!hasMultipleOwnedCollegeSavings && (
          <DisplayTaxResources
            taxResourcesList={TAX_REFUND_DIRECT_DEPOSIT_HAS_529_LINK}
            view={view}
          />
        )}
      </Fragment>
    );

    return hasOwnedCollegeSavingsOnly
      ? taxPlanning529Only
      : taxPlanningHasCollegeSavings;
  };

  const TaxPlanning = () =>
    !hasOwnedCollegeSavings ? (
      <TaxPlanningDefault />
    ) : (
      <TaxPlanning529
        hasMultipleOwnedCollegeSavings={hasMultipleOwnedCollegeSavings}
      />
    );

  return (
    <Fragment>
      {hasOwnedRetirementAccounts && (
        <div className="tile-summary-v2">
          <div className={"tax-resources-v2 tile-summary-content-v2"}>
            <MyTaxInformation />
            <div className={"content-divider-v2"}/>
            <TaxPlanning />
          </div>
        </div>
      )}
      {!hasOwnedRetirementAccounts && (
        <Fragment>
          <div className="tile-summary-v2">
            <div className={"tax-resources-v2 tile-summary-content-v2"}>
              <MyTaxInformation />
            </div>
          </div>
          <div className="tile-summary-v2">
            <div className={"tax-resources-v2 tile-summary-content-v2"}>
              <TaxPlanning />
            </div>
          </div>
        </Fragment>
      )}
    </Fragment>
  );
};

TaxResourcesV2.propTypes = {
  history: object,
  turboTaxLinkInfo: object,
  hasRetirementAccounts: bool.isRequired
};

TaxResourcesV2.defaultProps = {
  hasRetirementAccounts: false
};

const mapStateToProps = (state) => ({
  turboTaxLinkInfo: getTurboTaxLinkInfo(state),
  hasOwnedMaryland529Only: hasOwnedMaryland529Only(state),
  nonViewOnlyProducts: getNonViewOnlyProducts(state),
  hasOwnedCollegeSavings: getHasOwnedCollegeSavings(state),
  hasMultipleOwnedCollegeSavings: hasMultipleOwnedCollegeSavings(state),
  view: getView(state),
  hasOwnedRetirementAccounts: hasOwnedRetirementAccounts(state),
  hasOwnedCollegeSavingsOnly: getHasOwnedCollegeSavingsOnly(state),
  isRMDEligible: isRMDEligible(state),
  hasRMDEligibleAccount: hasRMDEligibleAccounts(state)
});

export default withRouter(connect(mapStateToProps)(TaxResourcesV2));
